import { useEffect } from 'react';
import { useForm, FormProvider, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { FormField } from '~/components/system/form-field';
import { MultiSelect } from '~/components/multi-select';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { ImageSlotField } from '~/components/image-slot-field';
import { customerKeys, useCustomer, useCustomerMutations, uploadCustomerImage } from '~/hooks/use-customers';
import { useCustomerCategoriesList } from '~/hooks/use-customer-categories';
import { useCustomerTiersList } from '~/hooks/use-customer-tiers';
import { useCustomerGenders } from '~/hooks/use-customer-groups';
import { usePriceTypesList } from '~/hooks/use-price-types';
import { usePaymentTypesList } from '~/hooks/use-payment-types';
import { useShippingTypesList } from '~/hooks/use-shipping-types';
import { useTakeawayStoresList } from '~/hooks/use-takeaway-stores';
import { customerSchema, EMPTY_CUSTOMER, tokenToForm, type CustomerFormValues } from '~/lib/schemas/customer';

// Non-numeric sentinel so it never collides with a real (possibly 0, e.g. gender "Empty") lookup id.
const NONE = '__none__';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	customerId: number | null;
}

interface Opt {
	id?: number;
	nm?: string | null;
}

/** Create / edit a customer. Loads the full record in edit mode; image upload is handled separately. */
export function CustomerEditorSheet({ open, onOpenChange, customerId }: Props) {
	const { t } = useTranslation();
	const isEdit = customerId != null && customerId > 0;
	const qc = useQueryClient();
	const { data: loaded, isLoading } = useCustomer(open ? customerId : null);
	const { create, update } = useCustomerMutations();

	const { data: categories } = useCustomerCategoriesList();
	const { data: tiers } = useCustomerTiersList();
	const { data: genders } = useCustomerGenders();
	const { data: priceTypes } = usePriceTypesList();
	const { data: paymentTypes } = usePaymentTypesList();
	const { data: shippingTypes } = useShippingTypesList();
	const { data: takeawayStores } = useTakeawayStoresList();

	const form = useForm<CustomerFormValues>({
		resolver: zodResolver(customerSchema) as Resolver<CustomerFormValues>,
		defaultValues: EMPTY_CUSTOMER,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;

	useEffect(() => {
		if (!open) return;
		if (isEdit) {
			if (loaded) reset(tokenToForm(loaded));
		} else {
			reset(EMPTY_CUSTOMER);
		}
	}, [open, isEdit, loaded, reset]);

	const onSubmit = handleSubmit(async values => {
		if (!isEdit && !values.psw?.trim()) {
			setError('psw', { message: t('validation.required') });
			return;
		}
		try {
			let savedId = values.id ?? null;
			if (isEdit) await update.mutateAsync({ form: values, original: loaded ?? null });
			else {
				const created = await create.mutateAsync(values);
				savedId = created?.id ?? null;
			}
			// Image bytes upload out-of-band against the saved id (server commits immediately for id > 0).
			if (savedId && values.img1File instanceof File) {
				await uploadCustomerImage(savedId, '1', values.img1File);
				await qc.invalidateQueries({ queryKey: customerKeys.detail(savedId) });
				await qc.invalidateQueries({ queryKey: customerKeys.all });
			}
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const numField = (name: keyof CustomerFormValues) => watch(name) as number | null | undefined;
	const LookupSelect = ({
		name,
		label,
		options,
		placeholder
	}: {
		name: keyof CustomerFormValues;
		label: string;
		options: Opt[];
		placeholder?: string;
	}) => {
		const v = numField(name);
		return (
			<FormField label={label}>
				<Select
					value={v != null && v > 0 ? String(v) : NONE}
					onValueChange={val => setValue(name, (val === NONE ? null : Number(val)) as never, { shouldDirty: true })}>
					<SelectTrigger className="w-full" aria-label={label}>
						<SelectValue placeholder={placeholder} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={NONE}>{t('common.none')}</SelectItem>
						{options
							.filter(o => o.id != null)
							.map((o, i) => (
								<SelectItem key={`${o.id}-${i}`} value={String(o.id)}>
									{o.nm}
								</SelectItem>
							))}
					</SelectContent>
				</Select>
			</FormField>
		);
	};

	const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
		<div className="flex flex-col gap-3 border-b border-border pb-4">
			<h3 className="text-sm font-semibold text-foreground">{title}</h3>
			{children}
		</div>
	);

	const SwitchRow = ({ name, label }: { name: keyof CustomerFormValues; label: string }) => (
		<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
			<Label htmlFor={`cust-${String(name)}`}>{label}</Label>
			<Switch
				id={`cust-${String(name)}`}
				checked={Boolean(watch(name))}
				onCheckedChange={next => setValue(name, next as never, { shouldDirty: true })}
			/>
		</div>
	);

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
				<SheetHeader className="border-b border-border px-5 py-4">
					<SheetTitle>{isEdit ? t('customers.list.editTitle') : t('customers.list.addTitle')}</SheetTitle>
					<SheetDescription>{t('customers.list.formSubtitle')}</SheetDescription>
				</SheetHeader>

				{isEdit && isLoading ? (
					<div className="flex flex-col gap-3 p-5">
						{Array.from({ length: 8 }).map((_, i) => (
							<Skeleton key={i} className="h-9 w-full rounded-md" />
						))}
					</div>
				) : (
					<FormProvider {...form}>
						<form onSubmit={onSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
							<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-5 py-4">
								<Section title={t('customers.list.sections.account')}>
									<div className="grid grid-cols-2 gap-3">
										<FormField label={t('customers.list.columns.email')} error={formState.errors.eml?.message}>
											<Input {...register('eml')} maxLength={50} aria-invalid={Boolean(formState.errors.eml)} autoFocus />
										</FormField>
										<FormField label={t('customers.list.fields.username')} error={formState.errors.UserAlias?.message}>
											<Input {...register('UserAlias')} maxLength={255} />
										</FormField>
										<FormField label={t('customers.list.columns.name')} error={formState.errors.fn?.message}>
											<Input {...register('fn')} maxLength={50} />
										</FormField>
										<FormField label={t('customers.list.columns.surname')} error={formState.errors.ln?.message}>
											<Input {...register('ln')} maxLength={50} />
										</FormField>
										<FormField
											label={isEdit ? t('customers.list.fields.passwordEdit') : t('customers.list.fields.password')}
											error={formState.errors.psw?.message}>
											<Input type="password" autoComplete="new-password" {...register('psw')} />
										</FormField>
									</div>
									<div className="grid grid-cols-2 gap-3">
										<SwitchRow name="act" label={t('customers.list.columns.active')} />
										<SwitchRow name="cfm" label={t('customers.list.columns.confirmed')} />
									</div>
								</Section>

								<Section title={t('customers.list.sections.classification')}>
									<div className="grid grid-cols-2 gap-3">
										<div className="col-span-2">
											<FormField label={t('customers.list.columns.category')}>
												<MultiSelect
													value={(categories ?? []).filter(c => watch('ccids').includes(c.id!))}
													onChange={next => setValue('ccids', next.map(c => c.id!), { shouldDirty: true })}
													options={(categories ?? []).filter(c => c.id != null)}
													getKey={c => String(c.id)}
													getLabel={c => c.nm ?? ''}
													placeholder={t('common.none')}
												/>
											</FormField>
										</div>
										<LookupSelect name="c_tier_id" label={t('customers.list.fields.tier')} options={tiers ?? []} />
										<LookupSelect name="gd" label={t('customers.list.fields.gender')} options={genders ?? []} />
										<FormField label={t('customers.list.fields.dob')}>
											<Input type="date" {...register('dob')} />
										</FormField>
										<FormField label={t('customers.list.fields.discount')}>
											<Input
												type="number"
												step="any"
												{...register('dsc', { setValueAs: v => (v === '' || v == null ? null : Number(v)) })}
											/>
										</FormField>
									</div>
								</Section>

								<Section title={t('customers.list.sections.commercial')}>
									<div className="grid grid-cols-2 gap-3">
										<LookupSelect name="prid" label={t('customers.list.fields.priceType')} options={priceTypes ?? []} />
										<LookupSelect name="pmid" label={t('customers.list.fields.paymentType')} options={paymentTypes ?? []} />
										<LookupSelect name="shid" label={t('customers.list.fields.shippingType')} options={shippingTypes ?? []} />
										<LookupSelect name="tsid" label={t('customers.list.fields.takeawayStore')} options={takeawayStores ?? []} />
									</div>
								</Section>

								<Section title={t('customers.list.sections.company')}>
									<div className="grid grid-cols-2 gap-3">
										<FormField label={t('customers.list.fields.company')}>
											<Input {...register('cnm')} maxLength={100} />
										</FormField>
										<FormField label={t('customers.list.fields.descriptiveTitle')}>
											<Input {...register('dtl')} maxLength={100} />
										</FormField>
										<FormField label={t('customers.list.fields.vat')}>
											<Input {...register('vat')} maxLength={20} />
										</FormField>
										<FormField label={t('customers.list.fields.irs')}>
											<Input {...register('irs')} maxLength={50} />
										</FormField>
										<FormField label={t('customers.list.fields.tel')}>
											<Input {...register('tel')} maxLength={20} />
										</FormField>
										<FormField label={t('customers.list.fields.mobile')}>
											<Input {...register('mob')} maxLength={20} />
										</FormField>
										<FormField label={t('customers.list.fields.fax')}>
											<Input {...register('fax')} maxLength={20} />
										</FormField>
									</div>
								</Section>

								<Section title={t('customers.list.sections.flags')}>
									<div className="grid grid-cols-2 gap-3">
										<SwitchRow name="rnl" label={t('customers.list.fields.newsletter')} />
										<SwitchRow name="popt" label={t('customers.list.fields.promoOptin')} />
										<SwitchRow name="rpm" label={t('customers.list.fields.personalizedMessages')} />
										<SwitchRow name="hidden_reader" label={t('customers.list.fields.hiddenReader')} />
									</div>
								</Section>

								<Section title={t('customers.list.sections.erp')}>
									<div className="grid grid-cols-2 gap-3">
										<FormField label="ERP 1">
											<Input {...register('erpcd')} maxLength={50} />
										</FormField>
										<FormField label="ERP 2">
											<Input {...register('erpcd2')} maxLength={50} />
										</FormField>
										<FormField label="ERP 3">
											<Input {...register('erpcd3')} maxLength={50} />
										</FormField>
										<FormField label="ERP 4">
											<Input {...register('erpcd4')} maxLength={50} />
										</FormField>
										<FormField label="ERP 5">
											<Input {...register('erpcd5')} maxLength={50} />
										</FormField>
									</div>
								</Section>

								<Section title={t('customers.list.sections.image')}>
									<ImageSlotField
										urlField="img1"
										fileField="img1File"
										delField="imgdel1"
										label={t('customers.list.fields.image')}
										uploadLabel={t('customers.list.uploadImage')}
										replaceLabel={t('customers.list.replaceImage')}
									/>
								</Section>
							</div>

							<SheetFooter className="border-t border-border px-5 py-4">
								<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
									{t('common.cancel')}
								</Button>
								<Button type="submit" disabled={create.isPending || update.isPending}>
									{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
								</Button>
							</SheetFooter>
						</form>
					</FormProvider>
				)}
			</SheetContent>
		</Sheet>
	);
}
